/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { of } from "rxjs";
import { EnvironmentalImpactByApplication } from "../../../../models/http/application/environmental-impact-by-application.model";
import { BarChart } from "../../../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../../../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../../../models/http/charts/pie-chart/pie-chart.model";
import { StrategicApplicationFileStatusResponse } from "../../../../models/http/strategic-applications/strategic-application-file-status-response.model";
import { StrategicApplicationFilesList } from "../../../../models/http/strategic-applications/strategic-application-files-list.model";
import { StrategicApplicationGeneralInformation } from "../../../../models/http/strategic-applications/strategic-application-general-information.model";
import { StrategicApplicationKpi } from "../../../../models/http/strategic-applications/strategic-application-kpi.model";
import { StrategicApplicationUploadResponse } from "../../../../models/http/strategic-applications/strategic-application-upload-response.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";

const MOCK_FILES_LIST: StrategicApplicationFilesList = {
	files: [
		{ id: "mock-strategic-file-1", name: "Strategic applications - FY24.xlsx" },
		{ id: "mock-strategic-file-2", name: "Transformation roadmap.xlsx" }
	]
};

const DEFAULT_UPLOAD_RESPONSE: StrategicApplicationUploadResponse = {
	fileId: "mock-strategic-file-id"
};

const DEFAULT_STATUS_RESPONSE: StrategicApplicationFileStatusResponse = {
	status: "DONE",
	message: "Processing completed"
};

const DEFAULT_KPI: StrategicApplicationKpi = {
	value: 875,
	unit: "kgCO2e"
};

const GENERAL_INFORMATION: StrategicApplicationGeneralInformation = {
	totalCo2Emission: DEFAULT_KPI,
	countApplications: {
		value: 12,
		unit: "applications"
	}
};

const APPLICATIONS_BAR_CHART: BarChart = {
	title: "Applications emissions",
	barSeries: [
		{
			name: "CO2e",
			bars: [
				{ name: "Customer portal", value: 320, unit: "kgCO2e" },
				{ name: "Finance automation", value: 240, unit: "kgCO2e" },
				{ name: "Employee hub", value: 185, unit: "kgCO2e" },
				{ name: "ERP core", value: 130, unit: "kgCO2e" }
			]
		}
	]
};

const DEPLOYMENT_PIE_CHART: PieChart = {
	title: "Hosting type",
	PieChartSlice: [
		{ name: "Cloud", value: 55, unit: "kgCO2e" },
		{ name: "On-premise", value: 35, unit: "kgCO2e" },
		{ name: "SaaS", value: 25, unit: "kgCO2e" }
	]
};

const SCOPE_PIE_CHART: PieChart = {
	title: "Scope",
	PieChartSlice: [
		{ name: "Scope 1", value: 80, unit: "kgCO2e" },
		{ name: "Scope 2", value: 140, unit: "kgCO2e" },
		{ name: "Scope 3", value: 340, unit: "kgCO2e" }
	]
};

interface StrategicAppsOverTimeEntry {
	date: string;
	emission: number;
}

const STRATEGIC_APPS_OVER_TIME_ENTRIES: StrategicAppsOverTimeEntry[] = [
	{ date: "2025-04", emission: 50997.962703799443 },
	{ date: "2025-05", emission: 52697.894793926105 },
	{ date: "2025-06", emission: 50997.962703799443 },
	{ date: "2025-07", emission: 52697.894793926105 },
	{ date: "2025-08", emission: 52697.894793926105 },
	{ date: "2025-09", emission: 50997.962703799443 },
	{ date: "2025-10", emission: 52687.0416853908 },
	{ date: "2025-11", emission: 50672.369447740435 },
	{ date: "2025-12", emission: 52290.886919277815 }
];

const TOTAL_EMISSIONS_OVERTIME_CHART: LineChart = {
	title: "Total Emissions Overtime by Applications",
	lineSeries: [
		{
			name: "Total",
			linePoints: STRATEGIC_APPS_OVER_TIME_ENTRIES.map(entry => ({
				name: entry.date,
				value: entry.emission,
				unit: "kgCO2e"
			}))
		}
	]
};

const ENVIRONMENTAL_IMPACT_BY_APPLICATION: EnvironmentalImpactByApplication = {
	totalEmission: 804.084,
	first: true,
	last: true,
	size: 20,
	totalElements: 4,
	totalPages: 1,
	number: 0,
	contents: [
		{
			application: "Jumbo German Machine Learning Application",
			numberOfLayers: 5,
			totalEmissions: 730.5
		},
		{ application: "Azure Data", numberOfLayers: 3, totalEmissions: 42.3 },
		{ application: "Customer Portal", numberOfLayers: 4, totalEmissions: 18.7 },
		{ application: "Finance Automation", numberOfLayers: 2, totalEmissions: 12.584 }
	]
};

export class StrategicApplications {
	public static getFiles(_options: HttpRequestOptions) {
		return of(MOCK_FILES_LIST);
	}

	public static uploadFile(_options: HttpRequestOptions) {
		return of(DEFAULT_UPLOAD_RESPONSE);
	}

	public static getFileStatus(_options: HttpRequestOptions) {
		return of(DEFAULT_STATUS_RESPONSE);
	}

	public static renameFile(_options: HttpRequestOptions) {
		return of(void 0);
	}

	public static deleteFile(_options: HttpRequestOptions) {
		return of(void 0);
	}

	public static exportFile(_options: HttpRequestOptions) {
		return of("mock-export-content");
	}

	public static downloadTemplate() {
		return of(new Blob());
	}

	public static getGeneralInformation(_options: HttpRequestOptions) {
		return of(GENERAL_INFORMATION);
	}

	public static getEmissionApplications(_options: HttpRequestOptions) {
		return of(APPLICATIONS_BAR_CHART);
	}

	public static getEmissionByScope(_options: HttpRequestOptions) {
		return of(SCOPE_PIE_CHART);
	}

	public static getEmissionByDeployment(_options: HttpRequestOptions) {
		return of(DEPLOYMENT_PIE_CHART);
	}

	public static getTotalEmissionsOvertime(_options: HttpRequestOptions) {
		return of(TOTAL_EMISSIONS_OVERTIME_CHART);
	}

	public static getEnvironmentalImpactByApplication(_options: HttpRequestOptions) {
		return of(ENVIRONMENTAL_IMPACT_BY_APPLICATION);
	}
}
